import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, MapPin, Zap, Loader2 } from "lucide-react";
import { Nav, Footer, Hinomaru } from "@/components/Nav";
import { api } from "@/lib/api";
import { useLang, T } from "@/i18n/LanguageContext";
import { ExperienceCart } from "@/components/ExperienceCart";
import { GalleryHomePreview } from "@/components/gallery/PublicGallery";

// V50.1 · Immagine hero rimessa a quella originale (rollback su richiesta utente)
const IMG_HERO = "https://res.cloudinary.com/uyyjmbuj/image/upload/f_auto,q_auto/IMG-20260730-WA0110_xg42re";
const IMG_FALLBACK_EVO = "https://res.cloudinary.com/uyyjmbuj/image/upload/f_auto,q_auto/IMG-20260730-WA0110_xg42re";
const IMG_FALLBACK_WRX = "https://res.cloudinary.com/uyyjmbuj/image/upload/f_auto,q_auto/IMG-20260730-WA0121_k29fwf";

export default function Home() {
    const { t, lang } = useLang();
    const [ev, setEv] = useState(null);
    const [cars, setCars] = useState({ EVO: null, WRX: null });
    const [mediaProducts, setMediaProducts] = useState([]);
    const [loadingProduct, setLoadingProduct] = useState(null);
    const [addMedia, setAddMedia] = useState(false); // checkbox aggiungi media al checkout
    // V55.5 · Prezzi live dal Motore #2 (pricing_tiers), unica fonte di verità
    const [pricing, setPricing] = useState(null);
    const [cartProduct, setCartProduct] = useState(null);

    useEffect(() => {
        api.get("/event").then(r => setEv(r.data)).catch(() => {});
        api.get("/cars").then(r => setCars(r.data)).catch(() => {});
        api.get("/media-products").then(r => setMediaProducts(r.data.items || [])).catch(() => {});
        api.get("/pricing/current").then(r => setPricing(r.data)).catch(() => {});
    }, []);

    // Helper: legge prezzo/listino live per un product_id, con fallback ai valori hardcoded
    const priceOf = (pid, fallback) => {
        const p = pricing?.products?.[pid];
        if (!p) return "…";
        return String(Math.round(p.price));
    };
    const listOf = (pid, fallback) => {
        const p = pricing?.products?.[pid];
        if (!p) return "…";
        return String(Math.round(p.list));
    };

    useEffect(() => {
        if (window.location.hash === "#prodotti") {
            setTimeout(() => {
                document.getElementById("prodotti")?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 300);
        }
    }, []);

    const buy = (product_id) => {
        if (!ev || !pricing?.products?.[product_id]) { toast.error("Prezzi in caricamento, riprova tra poco"); return; }
        setCartProduct(product_id);
    };
    const checkout = async (mediaId, discountCode) => {
        const product_id = cartProduct;
        setLoadingProduct(product_id);
        try {
            const r = await api.post("/checkout", {
                product_id,
                event_id: ev.id,
                addon_video: mediaId === "addon_video",
                addon_foto: mediaId === "addon_foto",
                addon_media_bundle: mediaId === "addon_media_bundle",
                discount_code: discountCode,
                origin_url: window.location.origin
            });
            window.location.href = r.data.checkout_url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore checkout");
            setLoadingProduct(null);
        }
    };

    const soldOut = (pid) => ev && (ev.capacity_left?.[pid] ?? 0) <= 0;

    const IMG_EVO = cars.EVO?.image_url || IMG_FALLBACK_EVO;
    const IMG_WRX = cars.WRX?.image_url || IMG_FALLBACK_WRX;
    const IMG_PASSENGER = cars.PASSENGER?.image_url || "https://images.unsplash.com/photo-1560890544-f9b53745706d?fm=jpg&q=85&w=1600&auto=format&fit=crop";
    const DESC_EVO = cars.EVO?.description || t("home.descEvoDefault");
    const DESC_WRX = cars.WRX?.description || t("home.descWrxDefault");
    const DESC_PASSENGER = cars.PASSENGER?.description || t("home.descPassengerDefault");
    const cartItems = {
        singola_evo: { name: "Mitsubishi Lancer EVO X", image: IMG_EVO },
        singola_wrx: { name: "Subaru Impreza WRX", image: IMG_WRX },
        combo_duello: { name: "EVO + WRX · due auto", image: IMG_EVO },
        passenger_experience: { name: "Esperienza passeggero", image: IMG_PASSENGER },
    };

    return (
        <div className="min-h-screen bg-[#15181C] text-[#F4F2ED]">
            <Nav />

            {/* Hero */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 opacity-30 bg-cover bg-center" style={{ backgroundImage: `url(${IMG_HERO})` }} />
                <div className="absolute inset-0 bg-gradient-to-b from-[#15181C]/40 via-[#15181C]/70 to-[#15181C]" />
                <div className="relative max-w-6xl mx-auto px-5 md:px-8 pt-14 pb-10">
                    <div className="flex flex-wrap items-center gap-3 mb-6">
                        <span data-testid="event-tag" className="px-3 py-1 border border-[#E2231A] text-[#E2231A] text-[10px] uppercase tracking-[0.3em]">{t("home.tagPreorder")}</span>
                    </div>
                    <h1 className="font-display text-6xl md:text-8xl uppercase leading-[0.9] mb-4">{t("home.heroTitle1")}<br /><span className="text-[#E2231A]">{t("home.heroTitle2")}</span></h1>
                    <Countdown targetIso="2026-09-13T09:00:00+02:00" />
                    <p className="text-[#D5D8DE] text-base md:text-lg max-w-xl mb-6" data-testid="hero-live-location">Mitsubishi Lancer Evo X e Subaru Impreza WRX · {ev?.circuit || "…"} · {ev?.city || "…"}</p>

                    {/* JDM Experience banner */}
                    <div data-testid="jdm-experience-banner" className="mb-6 inline-flex items-center gap-2 border-l-4 border-[#E2231A] bg-[#1C2026] pl-4 pr-5 py-2">
                        <Hinomaru size={14} />
                        <span className="text-xs md:text-sm font-display uppercase tracking-[0.2em]">{t("home.experienceBanner")}</span>
                    </div>

                    <div className="flex flex-wrap gap-4 mb-4">
                        <div data-testid="event-date" className="border border-[#9AA2AD]/30 bg-[#1C2026] px-4 py-3">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-[#9AA2AD]">{t("home.labelDate")}</div>
                            <div className="font-display text-2xl">{ev?.date ? new Date(ev.date).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: "Europe/Rome" }) : "…"}</div>
                        </div>
                        <div data-testid="event-place" className="border border-[#9AA2AD]/30 bg-[#1C2026] px-4 py-3">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-[#9AA2AD] flex items-center gap-1"><MapPin size={12} /> {t("home.labelCircuit")}</div>
                            <div className="font-display text-2xl">{ev?.circuit || "…"} · {ev?.city || "…"}</div>
                        </div>
                        <div data-testid="event-context" className="border border-[#9AA2AD]/30 bg-[#1C2026] px-4 py-3">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-[#9AA2AD]">{t("home.labelEvent")}</div>
                            <div className="font-display text-2xl">{ev?.context || "…"}</div>
                        </div>
                        <div data-testid="event-hours" className="border border-[#9AA2AD]/30 bg-[#1C2026] px-4 py-3">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-[#9AA2AD]">{t("home.labelHours")}</div>
                            <div className="font-display text-2xl">{ev?.hours || "…"}</div>
                        </div>
                    </div>
                    <p className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">{t("home.heroNote")}</p>
                </div>
            </section>

            {/* Products — 2-click */}
            <section id="prodotti" className="max-w-6xl mx-auto px-5 md:px-8 py-16">
                <div className="flex items-baseline justify-between mb-8 flex-wrap gap-3">
                    <h2 className="font-display text-4xl md:text-5xl uppercase">{t("home.productsTitle")}</h2>
                    {/* V55.5 · Badge dinamico sincronizzato col Motore #2 */}
                    {pricing?.active_mode === "happy_hour" ? (
                        <span className="text-[10px] uppercase tracking-[0.3em] text-[#f59e0b] animate-pulse" data-testid="pricing-badge-happy-hour">
                            ⚡ HAPPY HOUR — {pricing.products?.combo_duello?.discount_pct || 0}% OFF
                        </span>
                    ) : pricing?.active_mode === "track_day" ? (
                        <span className="text-[10px] uppercase tracking-[0.3em] text-[#E2231A]" data-testid="pricing-badge-track-day">
                            🏁 PREZZI TRACK DAY
                        </span>
                    ) : pricing?.active_mode === "listino" ? (
                        <span className="text-[10px] uppercase tracking-[0.3em] text-[#9AA2AD]" data-testid="pricing-badge-listino">
                            LISTINO UFFICIALE
                        </span>
                    ) : (
                        <span className="text-[10px] uppercase tracking-[0.3em] text-[#E2231A]" data-testid="pricing-badge-prevendita">
                            {lang === "it" ? "Prezzi in prevendita" : "Presale prices"}
                        </span>
                    )}
                </div>
                {/* Combo — featured full width */}
                <div className="mb-4">
                    <ComboCard tid="buy-combo" price={priceOf("combo_duello", "129")} list={listOf("combo_duello", "258")} onClick={() => buy("combo_duello")} loading={loadingProduct === "combo_duello"} disabled={soldOut("combo_duello")} />
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                    <BuyCard tid="buy-singola-evo" tier={t("home.tierEvo")} price={priceOf("singola_evo", "79")} list={listOf("singola_evo", "158")} desc={DESC_EVO} img={IMG_EVO} onClick={() => buy("singola_evo")} loading={loadingProduct === "singola_evo"} disabled={soldOut("singola_evo")} />
                    <BuyCard tid="buy-singola-wrx" tier={t("home.tierWrx")} price={priceOf("singola_wrx", "79")} list={listOf("singola_wrx", "158")} desc={DESC_WRX} img={IMG_WRX} onClick={() => buy("singola_wrx")} loading={loadingProduct === "singola_wrx"} disabled={soldOut("singola_wrx")} />
                    <BuyCard tid="buy-passenger" tier={t("home.tierPassenger")} price={priceOf("passenger_experience", "29")} list={listOf("passenger_experience", "58")} desc={DESC_PASSENGER} img={IMG_PASSENGER} onClick={() => buy("passenger_experience")} loading={loadingProduct === "passenger_experience"} disabled={soldOut("passenger_experience")} />
                </div>

                {/* Checkbox aggiungi Pacchetto Media al checkout */}
                <div className="mt-8">
                    <label className={`flex items-center gap-4 cursor-pointer border-2 ${addMedia ? "border-[#E2231A] bg-[#E2231A]/5" : "border-[#9AA2AD]/30"} p-4 transition-all hover:border-[#E2231A]`} data-testid="checkbox-media-home">
                        <input
                            type="checkbox"
                            checked={addMedia}
                            onChange={e => setAddMedia(e.target.checked)}
                            className="w-5 h-5 accent-[#E2231A]"
                            data-testid="checkbox-media-input"
                        />
                        <div className="flex-1">
                            <div className="font-display uppercase tracking-widest text-sm text-[#F4F2ED]">🎬 Aggiungi Pacchetto Foto+Video</div>
                            <div className="text-xs text-[#9AA2AD] mt-1">Fotoset di foto e video professionali HD del tuo turno · +€25 al totale (invece di €35)</div>
                        </div>
                        <div className="font-display text-2xl text-[#E2231A]">+€25</div>
                    </label>
                </div>

                <p className="mt-8 text-xs text-[#9AA2AD]" data-testid="home-media-cart-note">Foto HD e video del tuo turno: pacchetti selezionabili nel carrello.</p>
            </section>

            <GalleryHomePreview />
            {/* Chi Siamo — About */}
            <ChiSiamo />

            {/* Servizio Multimediale */}
            <MultimediaSection products={mediaProducts} />

            <Footer />
            {cartProduct && <ExperienceCart key={cartProduct} product={{ ...cartItems[cartProduct], amount_cents: Math.round(pricing.products[cartProduct].price * 100), list_cents: Math.round(pricing.products[cartProduct].list * 100) }} event={ev} mediaProducts={mediaProducts} initialMedia={addMedia ? "addon_media_bundle" : ""} loading={!!loadingProduct} onClose={() => setCartProduct(null)} onCheckout={checkout} />}
        </div>
    );
}

const MultimediaSection = ({ products = [] }) => (
    <section id="multimedia" data-testid="section-multimedia" className="relative border-t border-[#9AA2AD]/15 bg-[#15181C]">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-20">
            <div className="flex items-center gap-3 mb-4">
                <span className="w-8 h-px bg-[#E2231A]" />
                <span className="text-[10px] uppercase tracking-[0.4em] text-[#E2231A]">Ricordi</span>
            </div>
            <h2 className="font-display text-4xl md:text-6xl uppercase leading-[0.95] mb-4">
                Foto & Video <span className="text-[#E2231A]">HD</span> in Pista
            </h2>
            <p className="text-[#F4F2ED]/80 text-base md:text-lg max-w-2xl mb-12">
                Il nostro fotografo cattura ogni curva e ogni sorriso. Ricevi il tuo pacchetto in alta risoluzione dopo l'evento — pronto da postare sui social e taggarci <b className="text-[#F4F2ED]">@velorajdm</b>.
            </p>

            <div className="grid md:grid-cols-3 gap-4">
                {products.length === 0 ? (
                    // Placeholder in caricamento
                    <div className="col-span-3 text-center text-[#9AA2AD] py-12 text-sm uppercase tracking-widest">Caricamento pacchetti…</div>
                ) : (
                    products.map(p => (
                        <MediaCard
                            key={p.id}
                            icon={p.icon}
                            title={p.title}
                            price={p.price}
                            features={p.features || []}
                            featured={!!p.featured}
                            tid={`media-card-${p.id.replace("addon_", "")}`}
                        />
                    ))
                )}
            </div>

            <div className="mt-10 border-l-2 border-[#E2231A] pl-4 text-sm text-[#9AA2AD] max-w-2xl">
                <b className="text-[#F4F2ED]">Porta il turno con te.</b> Foto e video in alta risoluzione, con consegna digitale dopo l'evento.
            </div>
        </div>
    </section>
);

const MediaCard = ({ icon, title, price, features, featured, tid }) => (
    <div
        data-testid={tid}
        className={`border ${featured ? "border-[#E2231A] bg-gradient-to-br from-[#E2231A]/10 to-transparent" : "border-[#9AA2AD]/30 bg-[#1C2026]"} p-6 flex flex-col hover:border-[#E2231A]/70 transition-colors duration-200`}
    >
        {featured && (
            <div className="text-[9px] uppercase tracking-[0.3em] text-[#E2231A] mb-3">★ Miglior scelta</div>
        )}
        <div className="text-4xl mb-3">{icon}</div>
        <div className="font-display text-2xl uppercase tracking-wide">{title}</div>
        <div className="flex items-baseline gap-2 mt-2 mb-5">
            <span className="font-display text-6xl">€{price}</span>
        </div>
        <ul className="space-y-2 text-sm text-[#F4F2ED]/80">
            {features.map((f, i) => (
                <li key={i} className="flex items-start gap-2">
                    <span className="text-[#E2231A] font-bold flex-shrink-0">›</span>
                    <span>{f}</span>
                </li>
            ))}
        </ul>
    </div>
);

const ChiSiamo = () => {
    const { t } = useLang();
    const paragraphs = t("home.aboutBody") || [];
    return (
        <section id="chi-siamo" data-testid="section-chi-siamo" className="relative border-t border-[#9AA2AD]/15 bg-[#1A1D22]">
            <div className="max-w-4xl mx-auto px-5 md:px-8 py-20">
                <div className="flex items-center gap-3 mb-4">
                    <span className="w-8 h-px bg-[#E2231A]" />
                    <span className="text-[10px] uppercase tracking-[0.4em] text-[#E2231A]">{t("home.aboutEyebrow")}</span>
                </div>
                <h2 className="font-display text-4xl md:text-6xl uppercase leading-[0.95] mb-8" dangerouslySetInnerHTML={{ __html: t("home.aboutTitle") }} />
                <div className="space-y-5 text-[#F4F2ED]/90 text-base md:text-lg leading-relaxed max-w-3xl">
                    {paragraphs.map((p, i) => (
                        <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
                    ))}
                </div>
                <div className="mt-10 flex items-center gap-3 text-[#9AA2AD]">
                    <img src="/logo/velora-horizontal.png" alt="VELORA JDM" className="h-10 w-auto" data-testid="chi-siamo-logo" />
                </div>
            </div>
        </section>
    );
};

const BuyCard = ({ tier, price, list, desc, img, onClick, loading, disabled, tid }) => {
    const { t } = useLang();
    // V55.7 · Sconto dinamico: se price === list, non mostrare listino barrato né percentuale
    const priceN = Number(price) || 0;
    const listN = Number(list) || 0;
    const discount = listN > priceN && listN > 0 ? Math.round((1 - priceN / listN) * 100) : 0;
    return (
        <div data-testid={tid} className="relative border border-[#9AA2AD]/20 bg-[#1C2026] overflow-hidden group">
            <div className="h-40 bg-cover bg-center opacity-70" style={{ backgroundImage: `url(${img})` }} />
            <div className="p-5">
                <div className="text-xs uppercase tracking-[0.3em] text-[#9AA2AD]">{tier}</div>
                <div className="mt-2 flex items-baseline gap-3">
                    <span className="font-display text-5xl">€{price}</span>
                    {discount > 0 && <span className="line-through text-[#9AA2AD]">€{list}</span>}
                    {discount > 0 && <span className="text-[10px] uppercase tracking-widest text-[#E2231A]" data-testid={`${tid}-discount`}>−{discount}%</span>}
                </div>
                <p className="text-sm text-[#9AA2AD] mt-2">{desc}</p>
                <p className="text-[10px] uppercase tracking-widest text-[#E2231A] mt-3 font-display">{disabled ? t("home.soldOut") : t("home.limitedSeats")}</p>
                <button data-testid={`${tid}-btn`} onClick={onClick} disabled={loading || disabled} className="mt-4 w-full flex items-center justify-center gap-3 bg-[#E2231A] hover:bg-[#C11B15] disabled:bg-[#9AA2AD]/20 disabled:text-[#9AA2AD] text-[#F4F2ED] py-4 font-display text-xl uppercase tracking-wider transition-colors duration-150">
                    {loading ? <Loader2 className="animate-spin" size={20} /> : disabled ? t("home.soldOut") : <>{t("home.buyAt")} €{price} <ArrowRight size={18} /></>}
                </button>
            </div>
        </div>
    );
};

const ComboCard = ({ price, list, onClick, loading, disabled, tid }) => {
    const { t } = useLang();
    // V55.7 · Sconto dinamico
    const priceN = Number(price) || 0;
    const listN = Number(list) || 0;
    const discount = listN > priceN && listN > 0 ? Math.round((1 - priceN / listN) * 100) : 0;
    return (
        <div data-testid={tid} className="relative border border-[#E2231A] bg-gradient-to-r from-[#1C2026] to-[#E2231A]/10 p-6 md:p-8">
            <div className="absolute -top-3 left-6 text-[10px] uppercase tracking-[0.3em] bg-[#E2231A] px-2 py-0.5">{t("home.tierComboBadge")}</div>
            <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-[#9AA2AD]">{t("home.tierComboLabel")}</div>
                    <h3 className="font-display text-4xl md:text-5xl uppercase leading-none mt-1" dangerouslySetInnerHTML={{ __html: t("home.tierComboTitle") }} />
                    <p className="text-sm text-[#F4F2ED] mt-3 leading-relaxed max-w-xl" dangerouslySetInnerHTML={{ __html: t("home.tierComboDesc") }} />
                    <p className="text-xs text-[#9AA2AD] mt-2 uppercase tracking-widest">{t("home.tierComboMeta")}</p>
                    <div className="mt-3 flex items-baseline gap-3">
                        <span className="font-display text-5xl">€{price}</span>
                        {discount > 0 && <span className="line-through text-[#9AA2AD]">€{list}</span>}
                        {discount > 0 && <span className="text-[10px] uppercase tracking-widest text-[#E2231A]" data-testid={`${tid}-discount`}>−{discount}%</span>}
                    </div>
                    <p className="text-[10px] uppercase tracking-widest text-[#E2231A] mt-2 font-display">{disabled ? t("home.soldOut") : `🔥 ${t("home.tierComboRemaining")}`}</p>
                </div>
                <button data-testid={`${tid}-btn`} onClick={onClick} disabled={loading || disabled} className="flex items-center justify-center gap-3 bg-[#E2231A] hover:bg-[#C11B15] disabled:bg-[#9AA2AD]/20 disabled:text-[#9AA2AD] text-[#F4F2ED] px-8 py-5 font-display text-2xl uppercase tracking-wider transition-colors duration-150">
                    {loading ? <Loader2 className="animate-spin" size={22} /> : disabled ? t("home.soldOut") : <>{t("home.buyAt")} €{price} <Zap size={22} /></>}
                </button>
            </div>
        </div>
    );
};


function Countdown({ targetIso }) {
    const { t } = useLang();
    const [tm, setT] = useState(() => diff(targetIso));
    useEffect(() => {
        const id = setInterval(() => setT(diff(targetIso)), 1000);
        return () => clearInterval(id);
    }, [targetIso]);
    if (tm.expired) {
        return (
            <div data-testid="countdown-live" className="inline-flex items-center gap-2 px-4 py-2 border border-[#E2231A] bg-[#E2231A]/10 text-[#E2231A] mb-5 animate-pulse">
                <span className="w-2 h-2 rounded-full bg-[#E2231A]" /> <span className="font-display uppercase tracking-widest text-sm">{t("home.countdownExpired")}</span>
            </div>
        );
    }
    const units = t("home.countUnits");
    return (
        <div data-testid="countdown" className="flex flex-wrap items-center gap-3 mb-5">
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#9AA2AD]">{t("home.countdownLabel")}</span>
            <div className="flex gap-2">
                <CountBox val={tm.d} label={units.d} />
                <CountBox val={tm.h} label={units.h} />
                <CountBox val={tm.m} label={units.m} />
                <CountBox val={tm.s} label={units.s} pulse />
            </div>
        </div>
    );
}

function CountBox({ val, label, pulse }) {
    return (
        <div className={`border border-[#9AA2AD]/30 bg-[#1C2026] px-3 py-2 min-w-[62px] text-center ${pulse ? "border-[#E2231A]/60" : ""}`}>
            <div className={`font-display text-3xl leading-none ${pulse ? "text-[#E2231A]" : "text-[#F4F2ED]"}`}>{String(val).padStart(2, "0")}</div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-[#9AA2AD] mt-1">{label}</div>
        </div>
    );
}

function diff(iso) {
    const target = new Date(iso).getTime();
    const now = Date.now();
    let ms = target - now;
    if (ms <= 0) return { expired: true, d: 0, h: 0, m: 0, s: 0 };
    const d = Math.floor(ms / 86400000); ms -= d * 86400000;
    const h = Math.floor(ms / 3600000); ms -= h * 3600000;
    const m = Math.floor(ms / 60000); ms -= m * 60000;
    const s = Math.floor(ms / 1000);
    return { expired: false, d, h, m, s };
}
